/**
 * VerificationActions — Approve + Reject buttons for a tutor.
 * Reject opens a Dialog requiring a mandatory rejection_reason.
 *
 * Props:
 *   tutor         {Object}       the full tutor resource
 *   onApprove     {() => Promise<void>}
 *   onReject      {(reason: string) => Promise<void>}
 *   disabled      {boolean}
 */

import { useState } from 'react';
import Button from '../ui/Button';
import Dialog from '../ui/Dialog';

const VerificationActions = ({ tutor, onApprove, onReject, disabled = false }) => {
  const [approving, setApproving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!tutor) return null;

  const status = tutor.status;

  const handleApprove = async () => {
    setApproving(true);
    try {
      await onApprove();
    } finally {
      setApproving(false);
    }
  };

  const handleRejectOpen = () => {
    setReason('');
    setReasonError('');
    setRejectOpen(true);
  };

  const handleRejectClose = () => {
    if (submitting) return;
    setRejectOpen(false);
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError('Le motif de rejet est obligatoire.');
      return;
    }
    setSubmitting(true);
    setReasonError('');
    try {
      await onReject(trimmed);
      setRejectOpen(false);
      setReason('');
    } catch (err) {
      // Surface 422 on the reason field if present
      const fieldError = err?.validationErrors?.rejection_reason?.[0];
      setReasonError(
        fieldError ||
          err?.userMessage ||
          'Une erreur est survenue. Veuillez réessayer.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex gap-3 pt-4 border-t border-gray-200">
        {status === 'pending_verification' && (
          <>
            <Button
              variant="primary"
              onClick={handleApprove}
              disabled={disabled || approving}
            >
              {approving ? 'En cours…' : 'Approuver'}
            </Button>
            <Button
              variant="danger"
              onClick={handleRejectOpen}
              disabled={disabled || approving}
            >
              Rejeter
            </Button>
          </>
        )}

        {status === 'verified' && (
          <Button
            variant="danger"
            onClick={handleRejectOpen}
            disabled={disabled}
          >
            Suspendre
          </Button>
        )}
      </div>

      {/* Reject / Suspend Dialog */}
      <Dialog
        open={rejectOpen}
        onClose={handleRejectClose}
        title={status === 'verified' ? 'Suspendre le répétiteur' : 'Rejeter le répétiteur'}
      >
        <form onSubmit={handleRejectSubmit} noValidate>
          <div className="mb-4">
            <label
              htmlFor="rejection_reason"
              className="block text-sm font-montserrat font-medium text-[#4A4A4A] mb-1"
            >
              Motif <span className="text-red-600">*</span>
            </label>
            <textarea
              id="rejection_reason"
              name="rejection_reason"
              rows={4}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError('');
              }}
              className={[
                'w-full bg-[#F5F5F5] border rounded-xl py-3 px-4 font-montserrat text-sm text-[#4A4A4A]',
                'placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#EAF4FB] transition-all resize-none',
                reasonError
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-transparent focus:border-[#17386E]',
              ].join(' ')}
              placeholder="Expliquez la raison du rejet…"
              disabled={submitting}
              maxLength={2000}
            />
            {reasonError && (
              <p className="text-xs text-red-600 mt-1">{reasonError}</p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={handleRejectClose}
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="danger"
              disabled={submitting}
            >
              {submitting ? 'En cours…' : 'Confirmer'}
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default VerificationActions;
